
































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { firestore } from "firebase/app";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import DatePickerComponent from "@/components/common/DatePicker.vue";
import { ClientContractData } from "@/data/models/ClientAdministrationRow";
import { updateClientContracts } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  canSaveContracts,
  CONTRACT_TYPE_ORDER,
  contractChanges,
  contractsErrorKey,
  ContractForm,
  toContractForm,
} from "@/utils/ClientContracts";

type SinceField = "hasServiceContractForFirmwaresAndConfigsSince" | "hasWarrantyExtensionForPanelsSince";

/** What `show()` resolves with: the saved Verträge, CHANGED_MEANWHILE (the caller reloads), or null. */
export type ContractsDialogResult = ClientContractData | "CHANGED_MEANWHILE" | null;

const EMPTY_CONTRACTS: ClientContractData = {
  contractTypes: [],
  hasServiceContractForFirmwaresAndConfigs: false,
  hasServiceContractForFirmwaresAndConfigsSince: null,
  hasWarrantyExtensionForPanels: false,
  hasWarrantyExtensionForPanelsSince: null,
};

/**
 * Edits a client's Verträge (backlog #38, slice 2): the contract types, the service contract for
 * firmware and configurations and the warranty extension for Panels, each with an optional start date
 * (Taimoor, 2026-10-14). Only changed fields are sent, each with the value loaded, so IRES Cloud can
 * refuse one changed meanwhile; unticking a contract clears its date.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "date-picker": DatePickerComponent,
  },
})
export default class ClientContractsDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientContractData = { ...EMPTY_CONTRACTS };
  form: ContractForm = toContractForm(EMPTY_CONTRACTS);

  readonly contractTypes = CONTRACT_TYPE_ORDER;
  readonly contracts: Array<{ flag: "hasServiceContractForFirmwaresAndConfigs" | "hasWarrantyExtensionForPanels"; since: SinceField; labelKey: string }> = [
    {
      flag: "hasServiceContractForFirmwaresAndConfigs",
      since: "hasServiceContractForFirmwaresAndConfigsSince",
      labelKey: "admin.clients.detail.fields.serviceContract",
    },
    {
      flag: "hasWarrantyExtensionForPanels",
      since: "hasWarrantyExtensionForPanelsSince",
      labelKey: "admin.clients.detail.fields.warrantyExtensionForPanels",
    },
  ];

  private resolve: ((result: ContractsDialogResult) => void) | null = null;

  get hasChanged(): boolean {
    return Object.keys(contractChanges(this.stored, this.form)).length > 0;
  }

  get canSave(): boolean {
    return canSaveContracts(this.stored, this.form);
  }

  /** The shared DatePicker works in Timestamps; the form keeps epoch ms, as IRES Cloud serves them. */
  sinceTimestamp(field: SinceField): firestore.Timestamp | null {
    const millis = this.form[field];
    return millis === null ? null : firestore.Timestamp.fromMillis(millis);
  }

  onSinceInput(field: SinceField, value: firestore.Timestamp | null) {
    this.form[field] = value ? value.toMillis() : null;
  }

  /**
   * @param clientId - the client.
   * @param stored - its Verträge as the detail loaded them.
   */
  show(clientId: string, stored: ClientContractData): Promise<ContractsDialogResult> {
    this.clientId = clientId;
    this.stored = { ...stored, contractTypes: [...stored.contractTypes] };
    this.form = toContractForm(stored);
    this.baseDialog.show({
      title: this.$t("admin.clients.contracts.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await updateClientContracts(this.clientId, contractChanges(this.stored, this.form));
      SnackbarModule.showSuccess({ key: "admin.clients.contracts.saved" });
      this.close(saved ?? { ...this.form });
    } catch (error) {
      const key = contractsErrorKey(error);
      SnackbarModule.showError({ key });
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: ContractsDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: ContractsDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
